import pick from 'lodash/pick';
import { useEffect, useContext, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';

import StripeElements from 'components/common/forms/StripeElements';
import ToastMessage from 'components/common/ToastMessage';
import CheckoutForm from 'components/signUpFunnel/checkout/CheckoutForm';
import { ProgressBarContext, UserContext } from 'components/signUpFunnel/Contexts';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getBrandRoute } from 'helpers/routes';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { BEGIN_CHECKOUT_EVENT, FAMILY } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { availableZipCode, createEvents, getBox } from 'services/RequestService/endpoints/signUp';

const CheckoutContainer = () => {
  const analytics = useAnalytics();
  const { user, setUser } = useContext(UserContext);
  const { setProgress } = useContext(ProgressBarContext);
  const { brand } = useContext(BrandContext);
  const [zipCodeDeliverability, setZipCodeDeliverability] = useState({
    message: '',
    isDeliverable: null,
  });
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [box, setBox] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;
  const { isMobile } = useScreenSizeConstants();

  const history = useHistory();

  const isMobileAppUser = document
    .getElementById('hc-sign-up-funnel-react-app')
    ?.getAttribute('data-mobile-sign-up');

  useMetadata({
    title: `Step 6: Checkout | ${brandData(brand)?.displayName}`,
  });

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].checkout.viewed);
      }
    },
    [brand]
  );

  useEffect(() => {
    if (!box || !user) return;

    analytics.track(BEGIN_CHECKOUT_EVENT, { ...box, servings: user.servings });
  }, [box, user]);

  useEffect(() => {
    setProgress({ percent: '70' });
    createEvents({ params: { event_name: 'PageView' } });
  }, []);

  useEffect(() => {
    if (user && (!user.servings || !user.weeklyMeals))
      history.replace(getBrandRoute({ brand, isFamily: isFamilyMealPlan }).box);
  }, [user, brand]);

  useEffect(() => {
    const { servings, weeklyMeals, signupZip } = pick(user, [
      'servings',
      'weeklyMeals',
      'signupZip',
    ]);
    const fetchBox = async () => {
      try {
        const response = await getBox({
          params: {
            servings,
            weeklyMeals,
            zipCode: signupZip,
            estimated: false,
          },
        });
        if (response.ok) {
          setDisableSubmit(false);
          setBox(prevBox => ({ ...prevBox, ...response.box }));
        } else {
          history.replace(getBrandRoute({ brand, isFamily: isFamilyMealPlan }).box);
        }
      } catch (error) {
        setDisableSubmit(false);
        ErrorLogging.captureException(error.message);
      }
    };

    if (!user?.completedSignup && servings && weeklyMeals && signupZip) {
      setDisableSubmit(true);
      fetchBox();
    }
  }, [user, brand]);

  const handleZipCodeChange = useCallback(
    async zipCode => {
      try {
        setDisableSubmit(true);
        const response = await availableZipCode({
          params: { zip: zipCode, mealPlanSlug: user.mealPlanSlug },
        });
        if (response.ok) {
          setUser(prevUser => ({
            ...prevUser,
            signupZip: zipCode,
            signupCity: response.zipCodes.city,
            signupState: response.zipCodes.state,
          }));
          setZipCodeDeliverability({
            message: response.zipCodes.message,
            isDeliverable: true,
          });
        } else {
          setZipCodeDeliverability({
            message: response?.error,
            isDeliverable: false,
          });
        }
      } catch {
        setZipCodeDeliverability({
          isDeliverable: false,
        });
      } finally {
        setDisableSubmit(false);
      }
    },
    [user?.mealPlanSlug]
  );

  return (
    <div className="mx-auto w-full max-w-1100">
      <FunnelHeader
        title={isMobile ? 'Billing' : 'Billing Information'}
        containerClassName="mx-6 mb-6 pt-6 bp2:mb-12 bp2:pt-12 bp3:mx-0"
      />
      <StripeElements variant="refreshed">
        <CheckoutForm
          user={user}
          setUser={setUser}
          handleZipCodeChange={handleZipCodeChange}
          zipCodeDeliverability={zipCodeDeliverability}
          disableSubmit={disableSubmit}
          setDisableSubmit={setDisableSubmit}
          disabled={zipCodeDeliverability.isDeliverable === false}
          box={box}
          setBox={setBox}
          mobileSignUp={isMobileAppUser === 'true'}
          setToastMessage={setToastMessage}
        />
      </StripeElements>
      <ToastMessage
        content={toastMessage?.message}
        visible={Boolean(toastMessage)}
        type={toastMessage?.type}
      />
    </div>
  );
};

export default CheckoutContainer;
